/**
 * Session Store - Zustand store com persist middleware
 * @module features/chat/stores/sessionStore
 *
 * Store centralizado para gerenciamento de estado da sessao de assessment.
 * Utiliza Zustand com middleware de persistencia para LocalStorage.
 *
 * Features:
 * - Persistencia automatica em LocalStorage
 * - TTL de 24h para sessoes
 * - Geracao automatica de UUID para sessoes
 * - Seletores otimizados para performance
 */

import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { AssessmentType, ChatMessage, SessionState, UserData } from '../types';
import { INITIAL_USER_DATA } from '../types';

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Chave utilizada para persistencia no LocalStorage
 */
export const STORAGE_KEY = 'assessment-session';

/**
 * TTL da sessao em milissegundos (24 horas)
 */
export const SESSION_TTL_MS = 24 * 60 * 60 * 1000;

// =============================================================================
// STORE TYPES
// =============================================================================

/**
 * Campos permitidos para atualizacao via setUserField
 */
type UserDataField = keyof Pick<
  UserData,
  'name' | 'email' | 'company' | 'role' | 'cargo' | 'area' | 'tamanhoEmpresa' | 'dores'
>;

/**
 * Interface do estado e acoes do store
 */
interface SessionStoreState {
  /** Estado completo da sessao */
  session: SessionState;

  /** Flag que indica se o Zustand terminou de carregar do localStorage (FIX-005) */
  _hasHydrated: boolean;

  /** Flag que indica se havia dados REAIS no localStorage durante hydration (FIX-006) */
  _wasRestoredFromStorage: boolean;

  // Actions
  /**
   * Atualiza um campo especifico dos dados do usuario
   * @param field - Nome do campo a ser atualizado
   * @param value - Novo valor do campo (string para campos simples, string[] para campos como dores)
   */
  setUserField: (field: UserDataField, value: string | string[]) => void;

  /**
   * Define o consentimento LGPD do usuario
   * @param consent - true para dar consentimento, false para revogar
   */
  setConsent: (consent: boolean) => void;

  /**
   * Define o tipo de assessment selecionado
   * @param type - Tipo de assessment ('tech' ou 'governance')
   */
  setAssessmentType: (type: AssessmentType) => void;

  /**
   * Marca a sessao como completa, definindo completedAt
   */
  completeSession: () => void;

  /**
   * Limpa todos os dados da sessao e inicia uma nova
   * Remove dados do LocalStorage e gera novo UUID
   */
  clearSession: () => void;

  // Message Actions (T003-FE)
  /**
   * Adiciona uma mensagem ao historico do chat
   * @param message - Mensagem a ser adicionada
   */
  addMessage: (message: ChatMessage) => void;

  /**
   * Substitui todas as mensagens do chat
   * @param messages - Array de mensagens
   */
  setMessages: (messages: ChatMessage[]) => void;

  /**
   * Limpa todas as mensagens do chat
   */
  clearMessages: () => void;

  // Report Actions (T003-INT)
  /**
   * Atualiza a URL do relatorio gerado
   * @param reportUrl - URL do relatorio gerado
   */
  setReportUrl: (reportUrl: string) => void;

  /**
   * Marca que houve falha na geracao do relatorio
   * @param failed - true se houve falha
   */
  setReportGenerationFailed: (failed: boolean) => void;

  // Helpers
  /**
   * Verifica se a sessao expirou (mais de 24h desde startedAt)
   * @returns true se a sessao expirou
   */
  isSessionExpired: () => boolean;

  /**
   * Verifica se todos os campos obrigatorios foram preenchidos
   * Campos obrigatorios: name, email, consentGiven (true)
   * @returns true se todos os campos obrigatorios estao preenchidos
   */
  hasAllRequiredFields: () => boolean;

  /**
   * Verifica se existem mensagens no chat
   * @returns true se existem mensagens
   */
  hasMessages: () => boolean;

  /**
   * Define o estado de hydration (FIX-005)
   * @param hydrated - true quando Zustand terminou de carregar do localStorage
   */
  setHasHydrated: (hydrated: boolean) => void;

  /**
   * Define se a sessao foi restaurada do storage com dados reais (FIX-006)
   * @param restored - true quando havia dados reais no localStorage
   */
  setWasRestoredFromStorage: (restored: boolean) => void;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Gera um novo UUID para a sessao
 * Utiliza crypto.randomUUID() que e suportado em navegadores modernos
 */
function generateSessionId(): string {
  return crypto.randomUUID();
}

/**
 * Cria o estado inicial de uma nova sessao
 */
function createInitialSession(): SessionState {
  return {
    sessionId: generateSessionId(),
    userData: { ...INITIAL_USER_DATA },
    assessmentType: undefined,
    startedAt: new Date(),
    completedAt: undefined,
    messages: [],
    reportUrl: undefined,
    reportGenerationFailed: undefined,
    reportGeneratedAt: undefined,
  };
}

/**
 * Verifica se uma sessao esta expirada baseado no TTL de 24h
 * @param startedAt - Data de inicio da sessao
 * @returns true se a sessao expirou
 */
function checkSessionExpired(startedAt: Date): boolean {
  const now = Date.now();
  const sessionStart = new Date(startedAt).getTime();
  return now - sessionStart >= SESSION_TTL_MS;
}

// =============================================================================
// STORE CREATION
// =============================================================================

/**
 * Hook do Zustand store para gerenciamento de sessao
 *
 * @example
 * ```typescript
 * // Uso basico
 * const { session, setUserField, setConsent } = useSessionStore();
 *
 * // Atualizar nome
 * setUserField('name', 'Maria Silva');
 *
 * // Dar consentimento
 * setConsent(true);
 *
 * // Verificar se todos os campos obrigatorios estao preenchidos
 * const canProceed = useSessionStore(state => state.hasAllRequiredFields());
 * ```
 */
export const useSessionStore = create<SessionStoreState>()(
  persist(
    (set, get) => ({
      // Initial state
      session: createInitialSession(),
      _hasHydrated: false, // FIX-005: Inicia como false, sera true apos hydration
      _wasRestoredFromStorage: false, // FIX-006: Inicia como false, so sera true se houver dados reais

      // Actions
      setUserField: (field, value) =>
        set((state) => ({
          session: {
            ...state.session,
            userData: {
              ...state.session.userData,
              [field]: value,
            },
          },
        })),

      setConsent: (consent) =>
        set((state) => ({
          session: {
            ...state.session,
            userData: {
              ...state.session.userData,
              consentGiven: consent,
              consentTimestamp: consent ? new Date() : undefined,
            },
          },
        })),

      setAssessmentType: (type) =>
        set((state) => ({
          session: {
            ...state.session,
            assessmentType: type,
          },
        })),

      completeSession: () =>
        set((state) => ({
          session: {
            ...state.session,
            completedAt: new Date(),
            // FIX-004: NAO limpar mensagens aqui - sao necessarias para gerar relatorio
            // Mensagens sao limpas apenas em clearSession() quando usuario inicia novo assessment
            // Resetar apenas flags de geracao de report para permitir nova tentativa
            reportUrl: undefined,
            reportGenerationFailed: undefined,
            reportGeneratedAt: undefined,
          },
        })),

      clearSession: () => {
        // Remove from localStorage
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch {
          // Ignore localStorage errors
        }

        // Reset state
        set({
          session: createInitialSession(),
          _wasRestoredFromStorage: false, // FIX-006: Reset flag ao limpar sessao
        });
      },

      // Message Actions (T003-FE)
      addMessage: (message) =>
        set((state) => ({
          session: {
            ...state.session,
            messages: [...state.session.messages, message],
          },
        })),

      setMessages: (messages) =>
        set((state) => ({
          session: {
            ...state.session,
            messages,
          },
        })),

      clearMessages: () =>
        set((state) => ({
          session: {
            ...state.session,
            messages: [],
          },
        })),

      // Report Actions (T003-INT)
      setReportUrl: (reportUrl) =>
        set((state) => ({
          session: {
            ...state.session,
            reportUrl,
            reportGeneratedAt: new Date(),
            reportGenerationFailed: false, // Reset flag ao setar URL
          },
        })),

      setReportGenerationFailed: (failed) =>
        set((state) => ({
          session: {
            ...state.session,
            reportGenerationFailed: failed,
          },
        })),

      // Helpers
      isSessionExpired: () => {
        const { session } = get();
        return checkSessionExpired(session.startedAt);
      },

      hasAllRequiredFields: () => {
        const { session } = get();
        const { userData } = session;

        return (
          userData.name !== undefined &&
          userData.name !== '' &&
          userData.email !== undefined &&
          userData.email !== '' &&
          userData.consentGiven === true
        );
      },

      hasMessages: () => {
        const { session } = get();
        return session.messages.length > 0;
      },

      // FIX-005: Setter para hydration status
      setHasHydrated: (hydrated) => set({ _hasHydrated: hydrated }),

      // FIX-006: Setter para restored from storage status
      setWasRestoredFromStorage: (restored) => set({ _wasRestoredFromStorage: restored }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      // Partialize: persist only session data
      partialize: (state) => ({
        session: state.session,
      }),
      // Handle rehydration and check expiration
      onRehydrateStorage: () => (state) => {
        if (state) {
          // FIX-005: Marcar hydration como completa
          state.setHasHydrated(true);

          // FIX-006: Verificar se havia dados REAIS no localStorage
          // Uma sessao so e considerada "restaurada" se tinha mensagens, nome ou email
          const hasRealData =
            state.session.messages.length > 0 ||
            (state.session.userData.name !== undefined && state.session.userData.name !== '') ||
            (state.session.userData.email !== undefined && state.session.userData.email !== '');

          state.setWasRestoredFromStorage(hasRealData);

          // Check if session is expired
          const startedAt = new Date(state.session.startedAt);
          if (checkSessionExpired(startedAt)) {
            // Clear expired session - this will be done asynchronously
            setTimeout(() => {
              state.clearSession();
            }, 0);
          }
        }
      },
    }
  )
);

// =============================================================================
// SELECTORS
// =============================================================================

/**
 * Selector para obter apenas os dados do usuario
 * Otimizado para evitar re-renders desnecessarios
 *
 * @example
 * ```typescript
 * const userData = useSessionStore(getUserData);
 * ```
 */
export const getUserData = (state: SessionStoreState): UserData => state.session.userData;

/**
 * Selector para obter o ID da sessao
 *
 * @example
 * ```typescript
 * const sessionId = useSessionStore(getSessionId);
 * ```
 */
export const getSessionId = (state: SessionStoreState): string => state.session.sessionId;

/**
 * Selector para obter o tipo de assessment
 *
 * @example
 * ```typescript
 * const type = useSessionStore(getAssessmentType);
 * ```
 */
export const getAssessmentType = (state: SessionStoreState): AssessmentType | undefined =>
  state.session.assessmentType;

/**
 * Selector para verificar se a sessao foi completada
 *
 * @example
 * ```typescript
 * const isComplete = useSessionStore(isSessionComplete);
 * ```
 */
export const isSessionComplete = (state: SessionStoreState): boolean =>
  state.session.completedAt !== undefined;

/**
 * Selector para obter a data de inicio da sessao
 *
 * @example
 * ```typescript
 * const startedAt = useSessionStore(getStartedAt);
 * ```
 */
export const getStartedAt = (state: SessionStoreState): Date => state.session.startedAt;

/**
 * Selector para obter a sessao completa
 *
 * @example
 * ```typescript
 * const session = useSessionStore(getSession);
 * ```
 */
export const getSession = (state: SessionStoreState): SessionState => state.session;

/**
 * Selector para obter as mensagens do chat
 *
 * @example
 * ```typescript
 * const messages = useSessionStore(getMessages);
 * ```
 */
export const getMessages = (state: SessionStoreState): ChatMessage[] => state.session.messages;

/**
 * Selector para verificar se hydration foi concluida (FIX-005)
 *
 * @example
 * ```typescript
 * const hasHydrated = useSessionStore(getHasHydrated);
 * ```
 */
export const getHasHydrated = (state: SessionStoreState): boolean => state._hasHydrated;

/**
 * Selector para verificar se a sessao foi restaurada do storage com dados reais (FIX-006)
 * Retorna true apenas se havia mensagens, nome ou email no localStorage
 *
 * @example
 * ```typescript
 * const wasRestored = useSessionStore(getWasRestoredFromStorage);
 * ```
 */
export const getWasRestoredFromStorage = (state: SessionStoreState): boolean =>
  state._wasRestoredFromStorage;
